import React, { createContext, useState, useEffect, ReactNode, useReducer } from 'react'
import { PublicClientApplication } from '@azure/msal-browser'
import { AccountInfo } from '@azure/msal-common'
import { msalConfig } from '../utils/auth'
import { logger } from '../utils/logger'
import {
  getPlatformContact,
  getPlatforms,
  getServices,
  getSubProjAudit,
  getUserMappingDetails
} from '../services/api/dbOperator'
import { initialState, unifiedReducer } from '../components/reducer'

interface DataContext {
  userDetails: any
  accounts: AccountInfo[]
  platformValues: any
  subProjAuditValues: any
  platformContact: any
  serviceValues: any
  sessionId: any
}

interface AppContextType {
  data: DataContext
  setData: (newData: Partial<DataContext>) => void
  loading: boolean
  state: any
  dispatch: React.Dispatch<any>
}

const msalInstance = new PublicClientApplication(msalConfig)

const AppContext = createContext<AppContextType>({
  data: {
    userDetails: null,
    accounts: [],
    platformValues: null,
    subProjAuditValues: null,
    platformContact: null,
    serviceValues: null
  },
  setData: () => {},
  loading: true
})

interface ContextProviderProps {
  children: ReactNode
}

const ContextProviderComponent: React.FC<ContextProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DataContext>({
    userDetails: null,
    accounts: [],
    platformValues: null,
    platformContact: null,
    subProjAuditValues: null,
    serviceValues: null,
    sessionId: null
  })

  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        let userInformation = msalInstance.getAllAccounts()
        const localUserInformation = [
          {
            name: 'please add your name here',
            username: 'please add your email address here',
            homeAccountId: '',
            environment: 'local',
            tenantId: 'localid',
            localAccountId: '123'
          }
        ]

        userInformation =
          userInformation && userInformation.length > 0
            ? userInformation
            : localUserInformation

        const username = userInformation[0]?.username

        if (typeof username === 'string' && username.trim().length > 0) {
          const userDetails = await getUserMappingDetails(username)
          const platformValues = await getPlatforms()
          const subProjAuditValues = await getSubProjAudit()
          const platformContact = await getPlatformContact('', 0)
          const serviceValues = await getServices()

          setData({
            userDetails,
            accounts: userInformation,
            platformValues,
            subProjAuditValues,
            platformContact,
            serviceValues,
            sessionId: getSessionId(userInformation)
          })
        } else {
          console.warn('⚠️ No valid username found')
          setData((prev) => ({
            ...prev,
            accounts: userInformation
          }))
        }
        function getSessionId (userInformation: any): any {
          let sessionId = ''
          try {
            if (userInformation !== null && userInformation.length > 0 && userInformation[0].idTokenClaims) {
              sessionId = userInformation[0].idTokenClaims.sid
            } else {
              sessionId = generateUUID()
            }
            if (typeof window !== 'undefined') {
              sessionStorage.setItem('sessionId', sessionId)
              sessionStorage.setItem('name', userInformation[0].name)
              sessionStorage.setItem('email', userInformation[0].username)
            }
          } catch (error) {
            console.log(error)
          }
          return sessionId
        }
        function generateUUID (): string {
          try {
            if (typeof window !== 'undefined' && sessionStorage.getItem('sessionId')) {
              return  sessionStorage.getItem('sessionId')
            }
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
              const r = (Math.random() * 16) | 0
              const v = c === 'x' ? r : (r & 0x3) | 0x8
              return v.toString(16)
            })
          } catch (error) {
            console.log(error)
            return ''
          }
        }
      } catch (err) {
        console.error('❌ Error during context initialization:', err)
      } finally {
        setLoading(false)
      }
    }

    logger.log('Is loading state: ', loading)
    void init()
  }, [])

  const updateData = (newData: Partial<DataContext>): void => {
    setData((prevData) => ({
      ...prevData,
      ...newData
    }))
  }
  const [state, dispatch] = useReducer(unifiedReducer, initialState)

  // console.log('userDetails:', data.userDetails)

  return (
    <AppContext.Provider value={{ data, setData: updateData, loading, state, dispatch }}>
      {children}
    </AppContext.Provider>
  )
}

export { AppContext, ContextProviderComponent }
